import { setIn } from '@thi.ng/paths';
import type { JsonObject } from 'type-fest';

import { ConfigErrorTypes, type ConfigSemanticError } from '$lib/parse-config';

import { type ReadonlyFragment, ResourceTypes } from '../config-item';
import * as Schemas from '../config-schemas';
import {
  type ConfigParseSemanticError,
  type ConfigParseSuccess,
  toRawFragmentConfig,
  zodToSemanticErrors,
} from '../raw-config';

export const resourceTypeToRawResourceType = {
  [ResourceTypes.COMPONENT]: 'components',
  [ResourceTypes.SERVICE]: 'services',
  [ResourceTypes.REGISTRY_MODULE]: 'modules',
  [ResourceTypes.LOCAL_MODULE]: 'modules',
  [ResourceTypes.REMOTE]: 'remotes',
  [ResourceTypes.PROCESS]: 'processes',
  [ResourceTypes.PACKAGE]: 'packages',
  [ResourceTypes.TRIGGER]: 'triggers',
  [ResourceTypes.JOB]: 'jobs',
  [ResourceTypes.FRAGMENT_IMPORT]: 'fragments',
};

export const toModdableRawFragmentConfig = (
  fragment: ReadonlyFragment
): Schemas.ModdableRawFragmentConfig | undefined => {
  const result = Schemas.RawFragmentConfigSchema.safeParse(
    toRawFragmentConfig(fragment.originalConfig)
  );
  if (!result.success) {
    return undefined;
  }
  const rawFragmentConfig = result.data;

  let moddableRawFragmentConfig: Schemas.ModdableRawFragmentConfig = {
    components: {},
    services: {},
    modules: {},
    remotes: {},
    processes: {},
    packages: {},
    triggers: {},
    jobs: {},
  };
  for (const resourceType of Schemas.MappedResourceTypes) {
    // ReadonlyProcess names convert to raw process IDs
    if (resourceType === 'processes') {
      for (const resource of rawFragmentConfig[resourceType] ?? []) {
        moddableRawFragmentConfig = setIn(
          moddableRawFragmentConfig,
          [resourceType, resource.id],
          resource
        );
      }
    } else if (resourceType === 'modules') {
      // Group modules by name into arrays to preserve duplicates
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const modulesByName: Record<string, any[]> = {};
      for (const resource of rawFragmentConfig[resourceType] ?? []) {
        modulesByName[resource.name] ??= [];
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        modulesByName[resource.name]!.push(resource);
      }
      moddableRawFragmentConfig = setIn(
        moddableRawFragmentConfig,
        [resourceType],
        modulesByName
      );
    } else {
      for (const resource of rawFragmentConfig[resourceType] ?? []) {
        moddableRawFragmentConfig = setIn(
          moddableRawFragmentConfig,
          [resourceType, resource.name],
          resource
        );
      }
    }
  }
  return {
    ...rawFragmentConfig,
    ...moddableRawFragmentConfig,
  };
};

/**
 * formatFragmentModSemanticError modifies semantic error messages to be user-facing,
 * accounting for internal transformations needed to allow for resource name-indexing in mods.
 * It is needed for semantic errors caused by modifying top-level resource arrays,
 * which are transformed to name-keyed maps before applying fragment mods.
 *
 * ex.
 * 1. User adds a fragment with the config { components: [] }
 * 2. User adds the mod { $set: { "components": false } }.
 * 3. To apply the mod, we transform the fragment to name-keyed objects: { components: {} }
 * 4. We apply the mod, yielding an overwritten fragment: { components: false }
 * 5. We cannot transform this overwritten fragment back to its original resource list because we expect components to be a name-keyed map
 * 6. Zod returns the error "Expected object" for the value of components, but really the user should be told "Expected array"
 */
const formatFragmentModSemanticError = (
  error: ConfigSemanticError
): ConfigSemanticError => {
  const mappedResourceTypes = Schemas.MappedResourceTypes as string[];
  let updatedErrorMessage = error.message;
  if (
    error.path.length === 1 &&
    error.path[0] !== undefined &&
    mappedResourceTypes.includes(error.path[0].toString())
  ) {
    updatedErrorMessage = updatedErrorMessage.replace(
      'Expected object,',
      'Expected array,'
    );
  }
  return {
    ...error,
    message: `Fragment mod: ${updatedErrorMessage}`,
  };
};

export const fromModdableRawFragmentConfig = (
  moddableRawFragmentConfig: JsonObject
): ConfigParseSuccess<Schemas.RawFragmentConfig> | ConfigParseSemanticError => {
  const parseResult = Schemas.ModdableRawFragmentConfigSchema.safeParse(
    moddableRawFragmentConfig
  );
  if (!parseResult.success) {
    return {
      success: false,
      data: moddableRawFragmentConfig,
      errorType: ConfigErrorTypes.SEMANTIC,
      errors: zodToSemanticErrors(
        parseResult.error,
        formatFragmentModSemanticError
      ),
    };
  }
  const parsedModdableRawFragmentConfig = parseResult.data;

  let rawFragmentConfig: Schemas.RawFragmentConfig = {};
  for (const resourceType of Schemas.MappedResourceTypes) {
    if (resourceType === 'modules') {
      // Flatten module arrays back to single array, preserving duplicates
      const allModules = Object.values(
        parsedModdableRawFragmentConfig[resourceType]
      ).flat();
      if (allModules.length > 0) {
        rawFragmentConfig = setIn(
          rawFragmentConfig,
          [resourceType],
          allModules
        );
      }
    } else {
      const resources = Object.values(
        parsedModdableRawFragmentConfig[resourceType]
      );
      if (resources.length > 0) {
        rawFragmentConfig = setIn(rawFragmentConfig, [resourceType], resources);
      }
    }
  }
  for (const resourceType of Schemas.NonMappedResourceTypes) {
    rawFragmentConfig = setIn(
      rawFragmentConfig,
      [resourceType],
      parsedModdableRawFragmentConfig[resourceType]
    );
  }

  return {
    success: true,
    data: rawFragmentConfig,
  };
};
